// A deploy replaces every hashed chunk: a tab opened before it still points at
// the old file names, so its next lazy import 404s and Vite fires
// `vite:preloadError`. Reloading fetches the new index.html and its chunks.
// One reload per window: if the fresh page fails the same way, the error
// propagates to the error boundary instead of looping.

const LAST_RELOAD_KEY = "shaarp:preload-error-reload-at";
const RELOAD_WINDOW_MS = 10_000;

type PreloadErrorReloadDeps = {
  storage: Pick<Storage, "getItem" | "setItem">;
  reload: () => void;
  now: () => number;
};

/**
 * Reloads the page on a failed chunk load, unless it already did so within
 * `RELOAD_WINDOW_MS`. Returns whether it reloaded.
 */
export const handlePreloadError = (
  event: Event,
  { storage, reload, now }: PreloadErrorReloadDeps
): boolean => {
  const lastReloadAt = Number(storage.getItem(LAST_RELOAD_KEY) ?? 0);
  if (now() - lastReloadAt < RELOAD_WINDOW_MS) return false;

  storage.setItem(LAST_RELOAD_KEY, String(now()));
  event.preventDefault();
  reload();
  return true;
};

/** Registers the `vite:preloadError` listener once, before the app renders. */
export const registerPreloadErrorReload = (): void => {
  window.addEventListener("vite:preloadError", (event) => {
    let storage: PreloadErrorReloadDeps["storage"];
    try {
      storage = window.sessionStorage;
    } catch {
      // Storage blocked: no loop guard, so let the error surface.
      return;
    }
    handlePreloadError(event, {
      storage,
      reload: () => window.location.reload(),
      now: Date.now,
    });
  });
};
